import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { AdminDashboard } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { AreaChart, BarList, Gauge, Sparkline } from '@/components/charts/Chart';
import { AdminError } from './AdminError';

const RANGES = [
  [7, '7 days'],
  [30, '30 days'],
  [90, '90 days'],
] as const;

/**
 * Is ChiCare working?
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * Nothing. The console could answer "what needs approving" and "what did this
 * setting used to be", and nothing at all about whether the marketplace was
 * alive — so the first anybody would know of a bad week was a complaint.
 *
 * ── THE ORDER THINGS APPEAR IN, AND WHY ─────────────────────────────────────
 *
 * Queues first: four counts of work waiting for a person, because a pharmacy
 * that has been pending approval for three days is losing ChiCare money right
 * now and no trend line says so.
 *
 * Then trade, then who is on the platform, then health — which is the section
 * that goes wrong before anybody complains, and the reason to open this page on
 * a morning when nothing appears to be the matter.
 *
 * ── AND WHAT IS DELIBERATELY ABSENT ─────────────────────────────────────────
 *
 * No prescription content (§9.15), no buyer contact details, no refund
 * evidence. A dashboard is the easiest screen in any product to quietly turn
 * into a way of reading things nobody was supposed to read, because everything
 * on it feels like a statistic. Counts are statistics; the paper behind them is
 * not.
 *
 * And no buttons. Everything here reports — the screens that act on a pharmacy,
 * a refund or a listing have their own gates and their own written reasons
 * (§9.11).
 */
export function AdminDashboardScreen() {
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'dashboard', days],
    queryFn: ({ signal }) => api.adminDashboard(days, signal),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load the dashboard" error={error} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Dashboard</h1>
          <p className="m-0 mt-1 text-[13px] text-muted">
            Whether ChiCare is working — trade, sellers, and the things that break quietly.
          </p>
        </div>

        <div role="group" aria-label="How far back" className="flex gap-1 rounded-pill bg-paper p-1">
          {RANGES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              aria-pressed={days === value}
              className={cn(
                'min-h-9 rounded-pill px-3 text-[12.5px] font-bold',
                days === value ? 'bg-white text-ink shadow-sm' : 'bg-transparent text-muted',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {isLoading || !data ? <Loading /> : <Body data={data} />}
    </div>
  );
}

function Body({ data }: { data: AdminDashboard }) {
  return (
    <>
      <Queues queues={data.queues} />

      {/* Immediately under the queues, and above every chart. A queue is work
          somebody has to do; this is whether anything is broken — and the two
          go wrong on completely different timescales. */}
      <Monitoring monitoring={data.monitoring} />

      {/* Trade. Gross and revenue on one chart, because the interesting thing
          is the gap between them and two charts hide it. */}
      <section className="mb-4 rounded-card border border-line bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              Through the platform
            </p>
            <p className="m-0 mt-1 font-display text-[30px] font-bold leading-none tracking-[-0.02em] tabular">
              {data.trade.gross_display}
            </p>
            <Change bps={data.trade.change_bps} />
          </div>

          <div className="flex gap-5">
            <Figure label="ChiCare kept" value={data.trade.revenue_display} />
            <Figure label="Average order" value={data.trade.average_order_display} />
            <Figure label="Orders" value={`${data.orders.count}`} />
          </div>
        </div>

        <AreaChart
          className="mt-4"
          points={data.trade.gross_series}
          height={160}
          format={(value) => nairaish(value)}
        />

        {/* ChiCare's own share, at a glance, under the line it came out of. */}
        <div className="mt-2 flex items-center gap-2 border-t border-line pt-2.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Revenue
          </span>
          <Sparkline points={data.trade.revenue_series} className="h-6 w-full max-w-[320px]" tone="verified" />
        </div>
      </section>

      <div className="mb-4 grid gap-3 lg:grid-cols-3">
        {/* Health first of the three, because it is the one nobody thinks to
            look at and the only one that predicts a bad week. */}
        <section className="rounded-card border border-line bg-white p-4 lg:col-span-2">
          <p className="m-0 mb-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Health
          </p>

          <div className="flex flex-wrap items-start justify-around gap-4">
            <Gauge bps={data.health.accept_rate_bps} label="Orders accepted" />
            <Gauge bps={data.prescriptions.answer_rate_bps} label="Prescriptions answered" />
            {/* Inverted on purpose: a HIGH refund rate is the bad one, so the
                automatic green-at-90% colouring would be backwards here. */}
            <Gauge
              bps={data.health.refund_rate_bps}
              label="Refunded, of what sold"
              tone={
                (data.health.refund_rate_bps ?? 0) > 500
                  ? 'danger'
                  : (data.health.refund_rate_bps ?? 0) > 200
                    ? 'caution'
                    : 'verified'
              }
            />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-line pt-3 text-[12.5px] sm:grid-cols-4">
            <Fact
              label="Never accepted"
              value={`${data.health.never_accepted}`}
              bad={data.health.never_accepted > 0}
            />
            <Fact
              label="Capture failures"
              value={`${data.health.capture_failures}`}
              bad={data.health.capture_failures > 0}
            />
            <Fact label="Cancelled" value={`${data.health.orders_cancelled}`} />
            <Fact label="Refunded" value={data.health.refunded_display} />
          </dl>
        </section>

        <section className="rounded-card border border-line bg-white p-4">
          <p className="m-0 mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Marketplace
          </p>

          <dl className="space-y-2 text-[13px]">
            <Fact label="Buyers" value={`${data.marketplace.buyers_total}`} />
            <Fact label="Sellers live" value={`${data.marketplace.sellers_live}`} />
            <Fact
              label="Awaiting approval"
              value={`${data.marketplace.sellers_pending}`}
              bad={data.marketplace.sellers_pending > 0}
            />
            {/* Live but off sale — invisible in both counts above, which is
                exactly how a seller goes quiet without anybody noticing. */}
            <Fact
              label="Paused: licence"
              value={`${data.marketplace.sellers_paused}`}
              bad={data.marketplace.sellers_paused > 0}
            />
          </dl>

          <p className="m-0 mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            New buyers
          </p>
          <AreaChart points={data.marketplace.new_buyers_series} height={70} />
        </section>
      </div>

      <Activity activity={data.activity} />

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="rounded-card border border-line bg-white p-4 sm:p-5">
          <p className="m-0 mb-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Busiest sellers
          </p>

          {data.top_sellers.length === 0 ? (
            <Empty>Nothing has sold in this period.</Empty>
          ) : (
            <BarList
              rows={data.top_sellers.map((seller) => ({
                label: seller.name,
                value: seller.gross_kobo,
                display: seller.gross_display,
                hint: `${seller.orders} orders`,
              }))}
            />
          )}
        </section>

        <section className="rounded-card border border-line bg-white p-4 sm:p-5">
          <p className="m-0 mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Prescriptions
          </p>

          <div className="flex flex-wrap gap-5">
            <Figure label="Opened" value={`${data.prescriptions.opened}`} />
            <Figure label="Became orders" value={`${data.prescriptions.accepted}`} />
            <Figure
              label="Nobody replied"
              value={`${data.prescriptions.unanswered}`}
              bad={data.prescriptions.unanswered > 0}
            />
          </div>

          <AreaChart className="mt-3" points={data.prescriptions.series} height={96} />
        </section>
      </div>
    </>
  );
}

/**
 * Is anything broken?
 *
 * ── WHY EVERY NUMBER HERE SHOULD BE ZERO ────────────────────────────────────
 *
 * That is the design. A health panel whose healthy state is a column of noughts
 * can be read in two seconds from across a room, and a screen nobody has to
 * interpret is a screen people actually check. Anything non-zero turns amber —
 * or red, once.
 *
 * ── THE FIRST ONE IS NOT LIKE THE OTHERS ────────────────────────────────────
 *
 * `ledger_unbalanced` is §9.9: every posting's debits must equal its credits.
 * It is not a slow queue or a backed-up job. It means the books do not add up,
 * and nothing else on this page matters until it is zero again — which is why
 * it is first, why it is the only thing here that goes red rather than amber,
 * and why it says so in words when it fires.
 */
function Monitoring({ monitoring }: { monitoring: AdminDashboard['monitoring'] }) {
  const { money, flow, sellers, catalogue } = monitoring;

  const broken = money.ledger_unbalanced > 0;

  return (
    <section data-testid="admin-monitoring" className="mb-4">
      {broken ? (
        <div
          role="alert"
          className="mb-3 rounded-card border-2 border-danger bg-danger-tint px-4 py-3"
        >
          <p className="m-0 flex items-center gap-2 text-[14px] font-bold text-danger">
            <Icon name="alert" size={16} strokeWidth={2.2} />
            {money.ledger_unbalanced} ledger{' '}
            {money.ledger_unbalanced === 1 ? 'posting does' : 'postings do'} not balance
          </p>
          <p className="m-0 mt-1 text-[12.5px] leading-snug text-danger">
            Debits and credits disagree (§9.9). Stop and investigate — nothing else on this page
            means anything until this is zero.
          </p>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-card border border-line bg-white">
        <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Health and monitoring
          </p>
          <span
            className={cn(
              'rounded-pill px-2 py-[2px] font-mono text-[10px] font-bold uppercase tracking-[0.1em]',
              broken
                ? 'bg-danger-tint text-danger'
                : allClear(monitoring)
                  ? 'bg-verified-tint text-verified'
                  : 'bg-caution-tint text-caution-ink',
            )}
          >
            {broken ? 'Books do not balance' : allClear(monitoring) ? 'All clear' : 'Needs a look'}
          </span>
        </div>

        <div className="grid gap-x-6 gap-y-0 px-4 py-1 sm:grid-cols-2 lg:grid-cols-4">
          <Group title="Money">
            <Check label="Ledger unbalanced" value={money.ledger_unbalanced} severe />
            <Check label="Capture failures" value={money.capture_failures} />
            <Check label="Webhooks unprocessed" value={money.webhooks_unprocessed} />
            <Check label="Settlements overdue" value={money.settlements_overdue} />
            <Reading label="Awaiting settlement" value={money.awaiting_settlement_display} />
            <Reading label="Seller debt" value={money.seller_debt_display} />
          </Group>

          <Group title="Orders in flight">
            {/* Each of these is somebody waiting who has not been told. A
                stuck order and a completed one both count as one order. */}
            <Check label="Past accept deadline" value={flow.past_accept_deadline} />
            <Check label="Accepted, not packed" value={flow.accepted_not_packed} />
            <Check label="Custody overdue" value={flow.custody_overdue} />
            <Reading label="In flight" value={`${flow.in_flight}`} />
          </Group>

          <Group title="Sellers">
            {/* Live in every other count on the platform, and selling nothing
                at all, because a licence lapsed (§9.14). */}
            <Check label="Paused: licence" value={sellers.paused_for_licence} />
            <Check label="Licence expiring 30d" value={sellers.licence_expiring_30d} />
            <Check label="Awaiting approval" value={sellers.awaiting_approval} />
            <Check label="Suspended" value={sellers.suspended} />
            <Reading label="Live" value={`${sellers.live}`} />
          </Group>

          <Group title="Catalogue">
            <Check label="Submissions pending" value={catalogue.submissions_pending} />
            {/* Ships at the platform default, so the delivery bands somebody
                spent an afternoon setting do nothing for these products. */}
            <Check label="Products without weight" value={catalogue.products_without_weight} />
            <Reading label="Listings live" value={`${catalogue.listings_live}`} />
          </Group>
        </div>
      </div>
    </section>
  );
}

/** True when every count that should be zero is zero. */
function allClear(m: AdminDashboard['monitoring']): boolean {
  return (
    m.money.ledger_unbalanced === 0 &&
    m.money.capture_failures === 0 &&
    m.money.webhooks_unprocessed === 0 &&
    m.money.settlements_overdue === 0 &&
    m.flow.past_accept_deadline === 0 &&
    m.flow.accepted_not_packed === 0 &&
    m.flow.custody_overdue === 0 &&
    m.sellers.paused_for_licence === 0 &&
    m.sellers.suspended === 0 &&
    m.catalogue.submissions_pending === 0 &&
    m.catalogue.products_without_weight === 0
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-line py-3 last:border-b-0 sm:border-b-0">
      <p className="m-0 mb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
        {title}
      </p>
      <dl className="m-0 space-y-1 text-[12.5px]">{children}</dl>
    </div>
  );
}

/** A count that should be zero. Anything else is coloured. */
function Check({ label, value, severe = false }: { label: string; value: number; severe?: boolean }) {
  const wrong = value > 0;

  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className={cn(wrong ? 'font-medium text-ink' : 'text-muted')}>{label}</dt>
      <dd
        className={cn(
          'm-0 font-mono font-bold tabular',
          !wrong ? 'text-faint' : severe ? 'text-danger' : 'text-caution-ink',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/** A figure that is just a figure — no right answer, so no colour. */
function Reading({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-t border-line-2 pt-1">
      <dt className="text-muted">{label}</dt>
      <dd className="m-0 font-mono font-bold tabular text-ink">{value}</dd>
    </div>
  );
}

/**
 * The platform's pulse, most recent first.
 *
 * References and timestamps only. This says that something happened and where,
 * and the screens that show an order in full have their own checks — a
 * dashboard is the easiest place in any product to quietly turn a feed into a
 * way of reading things nobody was supposed to read.
 */
function Activity({ activity }: { activity: AdminDashboard['activity'] }) {
  if (activity.length === 0) return null;

  return (
    <section className="mb-4 rounded-card border border-line bg-white p-4 sm:p-5">
      <p className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        Latest activity
      </p>

      <ol className="m-0 list-none space-y-1.5 p-0">
        {activity.map((event, index) => (
          <li
            key={`${event.reference}-${event.at}-${index}`}
            className="flex items-baseline gap-2.5 text-[12.5px]"
          >
            <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              {when(event.at)}
            </span>
            <span className="shrink-0 text-ink">{said(event.type)}</span>
            <span className="min-w-0 flex-1 truncate text-muted">{event.pharmacy}</span>
            <span className="shrink-0 font-mono text-[11px] text-faint">{event.reference}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Event types are a contract; these are the words for them. */
function said(type: string): string {
  switch (type) {
    case 'order.placed':
      return 'Placed';
    case 'order.accepted':
      return 'Accepted';
    case 'order.dispatched':
      return 'Dispatched';
    case 'order.delivered':
      return 'Delivered';
    case 'order.cancelled':
      return 'Cancelled';
    case 'refund.executed':
      return 'Refunded';
    default:
      return type;
  }
}

function when(iso: string | null): string {
  if (!iso) return '';

  return new Date(iso).toLocaleString('en-NG', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Work waiting for a person, at the top and above every chart.
 *
 * A pharmacy pending approval for three days is losing ChiCare money right now,
 * and no trend line says so. These are counts with somewhere to go.
 */
function Queues({ queues }: { queues: AdminDashboard['queues'] }) {
  const rows = [
    { label: 'Pharmacies to approve', count: queues.pharmacies_pending, to: '/admin/pharmacies' },
    { label: 'Refunds to review', count: queues.refunds_pending, to: '/admin/refunds' },
    { label: 'Orders unaccepted', count: queues.orders_awaiting_accept, to: '/admin/prescriptions' },
    { label: 'Prescriptions unanswered', count: queues.prescriptions_unanswered, to: '/admin/prescriptions' },
  ];

  return (
    <div data-testid="admin-queues" className="mb-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
      {rows.map((row) => (
        <Link
          key={row.label}
          to={row.to}
          className={cn(
            'rounded-card border px-3.5 py-3 no-underline transition-colors',
            row.count > 0
              ? 'border-caution bg-caution-tint hover:border-caution-ink'
              : 'border-line bg-white',
          )}
        >
          <p
            className={cn(
              'm-0 font-display text-[24px] font-bold leading-none tracking-[-0.02em] tabular',
              row.count > 0 ? 'text-caution-ink' : 'text-ink',
            )}
          >
            {row.count}
          </p>
          <p
            className={cn(
              'm-0 mt-1 text-[11.5px] leading-snug',
              row.count > 0 ? 'text-caution-ink' : 'text-muted',
            )}
          >
            {row.label}
          </p>
        </Link>
      ))}
    </div>
  );
}

function Figure({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <div>
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">{label}</p>
      <p
        className={cn(
          'm-0 mt-0.5 font-display text-[19px] font-bold leading-none tracking-[-0.02em] tabular',
          bad ? 'text-caution-ink' : 'text-ink',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function Fact({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className={cn('m-0 font-mono font-bold tabular', bad ? 'text-caution-ink' : 'text-ink')}>
        {value}
      </dd>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="m-0 text-[13px] text-muted">{children}</p>;
}

function Change({ bps }: { bps: number | null }) {
  if (bps === null) {
    return <p className="m-0 mt-1.5 text-[12px] text-faint">No earlier period to compare with</p>;
  }

  const up = bps >= 0;

  return (
    <p className="m-0 mt-1.5">
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-pill px-1.5 py-[1px] text-[12px] font-bold',
          up ? 'bg-verified-tint text-verified' : 'bg-danger-tint text-danger',
        )}
      >
        <Icon name={up ? 'up' : 'down'} size={11} strokeWidth={2.4} />
        {Math.abs(Math.round(bps / 100))}% on the period before
      </span>
    </p>
  );
}

/** Kobo as a short naira string, for a chart readout only. See the seller
 *  dashboard for why this never touches a figure read against a statement. */
function nairaish(kobo: number): string {
  const naira = Math.round(kobo / 100);

  if (naira >= 1_000_000) return `₦${(naira / 1_000_000).toFixed(1)}m`;
  if (naira >= 1_000) return `₦${Math.round(naira / 1_000)}k`;

  return `₦${naira}`;
}

function Loading() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[78px] w-full" radius="card" />
        ))}
      </div>
      <Skeleton className="h-[280px] w-full" radius="card" />
      <div className="grid gap-3 lg:grid-cols-3">
        <Skeleton className="h-[240px] w-full lg:col-span-2" radius="card" />
        <Skeleton className="h-[240px] w-full" radius="card" />
      </div>
    </div>
  );
}
